// ═══════════════════════════════════════════════════════════════════════════
// grainCash.js — realizable CASH price for the Grain action engine (Kevin,
// 2026-08-17 spec): "the farm-level sale recommendation and economic
// comparison must use a realizable CASH price whenever basis is available."
//
// Futures stay the market SIGNAL (percentile/regime/target ladder, unchanged
// in grainTargets.js). This file answers a narrower question: given what this
// crop's board already priced, what could this farm ACTUALLY sell into today,
// and where does that number come from?
//
// REUSES the same two tables the Market Intelligence tool already reads —
// no parallel basis engine:
//   basis_calculated_daily — scraped local boards (WNY Energy, Star of the
//     West, ADM Buffalo), refreshed by cron (api/_corn-basis.js, _sotw-basis.js,
//     _adm-basis.js) via the shared recompute in api/_basis.js.
//   grain_basis_board — Howlett's own posted sheet. Read LIVE (not the daily
//     snapshot of it that also lands in basis_calculated_daily via
//     api/_howlett-basis.js) — the same choice src/App.jsx's GrainTargetPlanner
//     already made ("Kevin's rule — we don't need a basis scrape for Howlett,
//     it's literally in our app").
//
// GEOGRAPHY / PROVENANCE, NOT A RANKING SCORE. Kevin, 2026-08-17: Howlett is
// the only verified soybean source; corn/wheat may compare several LOCAL
// boards but never the Ohio Deerfield boards (src/App.jsx already excludes
// them from "local" the same way — api/_grain-brief.js: "far Ohio Deerfield
// boards"). ALLOWED_SOURCE below is that allowlist, not a computed weight —
// see CLAUDE.md rule zero: "do not invent weights or a black-box basis score."
//
// PURE — no fetch, no React, no Supabase. basisRows/boardRows are passed in
// already-fetched, the same shape App.jsx already fetches them in.
// ═══════════════════════════════════════════════════════════════════════════

import { grainBoardSymForMonth } from "../grainTargets";
// Static, Kevin-transcribed historical basis observations (Howlett bid
// sheets, 2021-2026) — HISTORY ONLY. Consumed exclusively by
// basisHistoryContext()'s comparable-sample lookup below, to deepen the
// Basis Opportunity gauge's percentile depth. Never merged into basisRows/
// boardRows, so a historical row can never become a live cash candidate —
// resolveGrainCash()'s candidate-building loops (boardCandidatesRaw /
// basisCandidatesRaw) read basisRows/boardRows directly and never see this
// array. 2026-08-19, Kevin: "basis runtime wiring only."
import { HISTORICAL_BASIS_ROWS } from "./basisHistorySeed";

// ── Elevator geography — DUPLICATED BY SOURCE from src/App.jsx:46600-46632
// (ELEVATOR_GEO / HOWLETT_TOWN / howlettTown / milesBetween / haversineMi),
// never edited independently, same convention grainRules.js already follows
// for the guarantee math. Kevin, 2026-08-17 reconciliation §A: "reuse existing
// repo geography facts/helpers... do not invent mileage cutoffs unless the
// repo already contains a defensible operational radius rule." App.jsx's own
// Local Cash Bids radius selector (src/App.jsx:46643) defaults to 30 mi —
// that EXISTING default, not an invented cutoff, is OPERATIONAL_RADIUS_MI
// below. Verified identical to source by tools/check-grain-cash.js (extracts
// the same names from App.jsx by source, the check-grain-rules.js technique).
const ELEVATOR_GEO = {
  wny_energy:           { lat: 43.2181, lon: -78.3867, name: "WNY Energy",            loc: "Medina, NY" },
  star_of_west:         { lat: 43.1017, lon: -77.8850, name: "Star of the West",      loc: "Churchville, NY" },
  adm_buffalo:          { lat: 42.8801, lon: -78.8785, name: "ADM Buffalo",           loc: "Buffalo, NY" },
  deerfield_deerfield:  { lat: 41.0339, lon: -81.0490, name: "Deerfield",             loc: "Deerfield, OH" },
  deerfield_kinsman:    { lat: 41.4570, lon: -80.5901, name: "Deerfield · Kinsman",   loc: "Kinsman, OH" },
  deerfield_louisville: { lat: 40.8376, lon: -81.2593, name: "Deerfield · Louisville",loc: "Louisville, OH" },
  deerfield_massillon:  { lat: 40.7967, lon: -81.5215, name: "Deerfield · Massillon", loc: "Massillon, OH" },
};
const HOWLETT_TOWN = {
  avon:    { lat: 42.9120, lon: -77.7458, name: "Howlett · Avon" },
  leroy:   { lat: 42.9784, lon: -77.9803, name: "Howlett · LeRoy" },
  batavia: { lat: 43.0004, lon: -78.1875, name: "Howlett · Batavia" },
  geneva:  { lat: 42.8689, lon: -76.9780, name: "Howlett · Geneva" },
};
function howlettTown(loc) {
  const s = (loc || "").toLowerCase();
  if (s.includes("avon") || s.includes("lakev")) return "avon";
  if (s.includes("leroy") || s.includes("le roy") || s.includes("td ag")) return "leroy";
  if (s.includes("batavia")) return "batavia";
  if (s.includes("geneva")) return "geneva";
  return null; // pickup-only labels (P/U, Picked Up, Lima) have no fixed town
}
const haversineMi = (a, b) => {
  const R = 3958.8, d2r = Math.PI / 180;
  const dLat = (b[0] - a[0]) * d2r, dLon = (b[1] - a[1]) * d2r;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * d2r) * Math.cos(b[0] * d2r) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
};
function milesBetween(a, b) {
  if (!a || !b || a.lat == null || b.lat == null) return null;
  return haversineMi([a.lat, a.lon], [b.lat, b.lon]);
}
const OPERATIONAL_RADIUS_MI = 30;

// Contract codes appear in TWO formats in the live data: basis_calculated_daily
// (and every scraped board) carries the full <root><monthLetter><yy> code (e.g.
// "CZ26" corn Dec26 — api/_futures-rollover.js), but Howlett's photographed
// sheet (grain_basis_board.cme_contract) never carries the crop-root letter —
// it's written straight off the sheet as "Z26". Comparing the trailing
// <letter><yy> is the one thing both formats agree on; do not try to guess or
// re-derive Howlett's missing root instead.
const CONTRACT_SUFFIX_RE = /([FGHJKMNQUVXZ])(\d{2})$/;
function contractSuffix(code) {
  const m = String(code || "").toUpperCase().match(CONTRACT_SUFFIX_RE);
  return m ? m[1] + m[2] : null;
}

// grainTargetLadder/grainBoardSymForMonth key soybeans 'soybeans'; so does
// grain_basis_board.commodity. basis_calculated_daily keys the same crop
// 'soy' (see src/App.jsx's own basisCommodity comment — "getting this wrong
// silently yields zero bids and the cash column just vanishes").
const LADDER_COMMODITY = { corn: "corn", wheat: "wheat", soybeans: "soybeans", soy: "soybeans" };

// ── Bid-structure normalization (docs/grain-gauge-build.md, Kevin 2026-08-18:
// "Is this observed basis historically attractive versus COMPARABLE basis?"
// — never blend a DELIVERED bid with a FARM PICKUP bid into one sample; a
// pickup bid embeds logistics value a delivered bid does not, so the two are
// not apples-to-apples even at the identical dollar basis).
//
// Read from EXPLICIT existing fields only — never inferred from a buyer's
// name. `grain_basis_board.term` already carries exactly this ('delivered' |
// 'pickup' — src/App.jsx's own GRAIN_TERM_LBL / pickTerm UI reads the same
// field, e.g. src/App.jsx:73656-73658), so a live Howlett board row is
// classified straight from that column. `basis_calculated_daily`'s scraped-
// board rows carry NO such field at all (verified against api/_basis.js's
// row shape and every scraper that writes it — source, commodity,
// delivery_period, bid_date, cash_price, delivery_contract,
// delivery_futures_price, delivery_basis, nothing else). SOURCE_BID_STRUCTURE
// below is a documented FACT about one specific named elevator
// (docs/grain-data-notes.md, Kevin 2026-08-18: "Medina ethanol-plant history
// is a DELIVERED bid series") — the same per-source-fact convention
// ELEVATOR_GEO/ALLOWED_SOURCE already use, not a guess from the name. Every
// other scraped source (star_of_west, adm_buffalo) is NOT documented either
// way and stays UNKNOWN rather than assumed.
export const BID_STRUCTURE = { DELIVERED: "delivered", PICKUP: "pickup", DIRECT_SHIP: "direct_ship", UNKNOWN: "unknown" };
const SOURCE_BID_STRUCTURE = { wny_energy: BID_STRUCTURE.DELIVERED };
export function normalizeBidStructure(raw) {
  const s = String(raw || "").trim().toLowerCase().replace(/[\s_-]+/g, " ");
  if (s === "delivered") return BID_STRUCTURE.DELIVERED;
  if (s === "pickup" || s === "pick up" || s === "picked up" || s === "farm pickup") return BID_STRUCTURE.PICKUP;
  if (s === "direct ship" || s === "directship") return BID_STRUCTURE.DIRECT_SHIP;
  return BID_STRUCTURE.UNKNOWN;
}
function resolveBidStructure(source, explicitTerm) {
  const fromField = normalizeBidStructure(explicitTerm);
  if (fromField !== BID_STRUCTURE.UNKNOWN) return fromField;
  return SOURCE_BID_STRUCTURE[canonicalSourceKey(source)] || SOURCE_BID_STRUCTURE[source] || BID_STRUCTURE.UNKNOWN;
}

// ── canonicalSourceKey — ONE physical bid column, however it was spelled.
//
// The source slug is derived from `grain_basis_board.location` by
// slugification, and Howlett's own sheet spells the SAME facility three
// different ways across its crop sections: "Avon & Lkville" (corn),
// "Avon & Lakeville" (soybeans), "Avon/Lakevlle" (wheat) — measured against
// the live board 2026-08-19. Those become howlett_avon_lkville /
// howlett_avon_lakeville / howlett_avon_lakevlle, and since basis
// comparability keys on the slug, one location's history was split across
// three identities that can never pool. Kevin's hand-transcribed seed
// (basisHistorySeed*.js) uses a FOURTH set of spellings again —
// howlett_avon, howlett_leroy, howlett_pickup_west — so of seven seeded
// locations only batavia and geneva could ever reach their own live
// candidate. That is why the Basis gauge read "1145 historical row(s) on
// file ... none comparable" on a farm whose history had genuinely been
// transcribed.
//
// This is deliberately the SAME town-matching rule howlettTown() already
// applies for geography (avon/lakev -> avon, leroy/le roy/td ag -> leroy),
// extended to the pickup columns, rather than a second spelling table that
// could drift from it. It normalizes SPELLING ONLY — it never merges two
// genuinely different bid columns: P/U West and P/U East stay separate keys
// because they are separate bids with separate basis (seed 2026-07-10:
// Dec-2026 -0.25 west vs -0.22 east), and a delivered town never merges
// with a pickup column because structure is a second, independent gate in
// filterComparableBasisHistory.
//
// A slug this cannot confidently place is returned UNCHANGED — an unknown
// Howlett column keeps its own identity and simply pools with nothing,
// which is the same honest outcome as before rather than a wrong merge.
// ENUMERATED, never pattern-guessed. A regex over town names looks tidier and
// is wrong on the live data: three observed slugs name MORE THAN ONE
// destination — howlett_avon_lakeville_batavia, howlett_avon_lkvlle_star,
// and (harmlessly) howlett_leroy_tdag — and an /avon|lakev/ rule folds the
// first two into the plain Avon column, which is precisely the "never merge
// two different bid columns" rule this function exists to uphold, broken by
// the function meant to uphold it. A combined or unrecognized column is left
// as its own identity: it pools with nothing, which is the same honest
// outcome as before this map existed.
//
// Every LEFT-hand slug below was observed in live data or in the transcribed
// seed on 2026-08-19 — 15 distinct howlett_* slugs in basis_calculated_daily
// (12 of which join a grain_basis_board column exactly) plus the 7 the seed
// files use. Nothing here is speculative; a slug that starts appearing later
// falls through to "unchanged" until someone places it deliberately.
const HOWLETT_CANONICAL = {
  // Avon / Lakeville — Howlett's own sheet spells this facility three ways
  // across its corn, soybean and wheat sections.
  howlett_avon: "howlett_avon",
  howlett_avon_lkville: "howlett_avon",
  howlett_avon_lakeville: "howlett_avon",
  howlett_avon_lakevlle: "howlett_avon",
  // LeRoy / TD Ag — two spellings, one column.
  howlett_leroy: "howlett_leroy",
  howlett_leroy_td_ag: "howlett_leroy",
  howlett_leroy_tdag: "howlett_leroy",
  howlett_batavia: "howlett_batavia",
  howlett_geneva: "howlett_geneva",
  // Pickup columns. West and East are DIFFERENT bids with different basis
  // (seed 2026-07-10, Dec-2026: -0.25 west vs -0.22 east) and must never
  // share a key. "Picked Up" is the wheat sheet's single pickup column.
  howlett_pickup_west: "howlett_pickup_west",
  howlett_p_u_west: "howlett_pickup_west",
  howlett_pickup_east: "howlett_pickup_east",
  howlett_p_u_east: "howlett_pickup_east",
  howlett_pickup: "howlett_pickup",
  howlett_picked_up: "howlett_pickup",
  howlett_dry_p_u_east_of_lima: "howlett_pickup_lima",
  // Plenish is a distinct crop contract; its Lakeville column must not fold
  // into the ordinary Avon/Lakeville corn+soy column that shares the name.
  howlett_plenish_lkville: "howlett_plenish_delivered",
  howlett_plenish_p_u: "howlett_plenish_pickup",
  // Deliberately ABSENT, and this is the point of enumerating:
  //   howlett_avon_lakeville_batavia — names two destinations
  //   howlett_avon_lkvlle_star       — names Avon and a Star of the West leg
  // Both keep their own identity rather than being folded into howlett_avon.
};
export function canonicalSourceKey(source) {
  const s = String(source || "").trim().toLowerCase();
  return HOWLETT_CANONICAL[s] || s;
}

// ── buildBidStructureIndex — bid structure READ from the source document,
// not from a hardcoded per-source table.
//
// `grain_basis_board.term` carries delivered/pickup for EVERY Howlett
// column, straight off Howlett's own sheet (measured 2026-08-19: 15 of 15
// location×commodity rows populated). resolveGrainCash's live-board branch
// already reads it per candidate. What it did NOT do was give the same
// answer to the HISTORY rows: basis_calculated_daily has no term column, so
// every historical Howlett row resolved UNKNOWN through SOURCE_BID_STRUCTURE
// (which documents exactly one source, wny_energy). The selected candidate
// therefore said "delivered" while every one of its own historical rows said
// "unknown", and filterComparableBasisHistory — correctly, since an
// unlabeled row must never be assumed to match — excluded all of them.
//
// So this is not a loosening of the comparability rule. The rule is
// unchanged; it is now being given the structure the board already states,
// keyed on canonicalSourceKey so a history row written under one spelling
// still finds its own location's term.
export function buildBidStructureIndex(boardRows = []) {
  const index = new Map();
  for (const r of boardRows || []) {
    if (!r || !r.location || !r.term) continue;
    const structure = normalizeBidStructure(r.term);
    if (structure === BID_STRUCTURE.UNKNOWN) continue;
    const slug = "howlett_" + String(r.location).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    const key = canonicalSourceKey(slug);
    const prior = index.get(key);
    // A location that reports two DIFFERENT terms across crop sections is a
    // contradiction in the source, not something to average or last-write.
    // Refuse it — the column stays UNKNOWN and reports nothing, rather than
    // silently adopting whichever crop section happened to be read last.
    if (prior && prior !== structure) { index.set(key, BID_STRUCTURE.UNKNOWN); continue; }
    index.set(key, structure);
  }
  for (const [k, v] of [...index]) if (v === BID_STRUCTURE.UNKNOWN) index.delete(k);
  return index;
}

// ── Comparable basis-history filter — the accuracy rule docs/grain-gauge-
// build.md exists for: "Never put delivered Medina and pickup Howlett into
// the same percentile sample and call one stronger for the farm." A
// historical row is comparable to the selected candidate only when crop,
// source (which already folds in location — each Howlett town is its own
// source slug), bid structure (BOTH known and EQUAL — an unlabeled row is
// never assumed to match), and the delivery/futures reference all agree.
// Structure is checked in addition to source so that if a future ingestion
// path starts recording pickup/delivered on the SAME source slug (today's
// live-board reader can already receive both under one town), a delivered
// row still can't enter a pickup sample or vice versa.
export function filterComparableBasisHistory({ crop, source, bidStructure, deliveryContract, rows = [] }) {
  const selStructure = normalizeBidStructure(bidStructure);
  const selSuffix = deliveryContract ? contractSuffix(deliveryContract) : null;
  const comparable = [], excluded = [];
  for (const r of rows || []) {
    if (!r) continue;
    const rStructure = normalizeBidStructure(r.bidStructure);
    const cropOk = crop == null || r.crop == null || String(r.crop).toLowerCase() === String(crop).toLowerCase();
    // Compared on the CANONICAL key, not the raw slug — same physical bid
    // column, however Howlett's sheet spelled it that week (see
    // canonicalSourceKey). A non-Howlett slug canonicalizes to itself, so
    // every other source's behavior is byte-identical to before.
    const sourceOk = source == null || r.source == null || canonicalSourceKey(r.source) === canonicalSourceKey(source);
    const structureOk = selStructure !== BID_STRUCTURE.UNKNOWN && rStructure === selStructure;
    // BOTH spellings, and this was a silent hole until 2026-08-19. The two
    // row sources disagree on case: the transcribed seed writes
    // `deliveryContract`, but basis_calculated_daily — every live scraped
    // row — writes `delivery_contract`. Reading only the camelCase key meant
    // `!r.deliveryContract` was true for EVERY live row, so the delivery
    // reference gate passed unconditionally and a Dec-26 candidate's
    // percentile pooled Jul-27 and Mar-27 basis from the same elevator.
    // Different delivery periods are different bids — that is the whole
    // reason this gate exists. It never fired on scraped data until now, and
    // nothing failed, because the ONLY rows it was ever applied to were the
    // handful of seed rows that happened to use the other spelling.
    const rowRef = r.deliveryContract || r.delivery_contract || null;
    const refOk = !selSuffix || !rowRef || contractSuffix(rowRef) === selSuffix;
    if (cropOk && sourceOk && structureOk && refOk) comparable.push(r);
    else excluded.push({
      row: r,
      reason: !structureOk
        ? "bid structure unknown or mismatched (e.g. delivered vs. pickup) — not apples-to-apples"
        : (!sourceOk ? "different source/location" : (!refOk ? "different delivery contract" : "different crop")),
    });
  }
  return { comparable, excluded };
}

const KNOWN_SOURCE_LABEL = {
  wny_energy: "WNY Energy (Medina, NY)",
  star_of_west: "Star of the West (Churchville, NY)",
  adm_buffalo: "ADM Buffalo (Buffalo, NY)",
};
function howlettLabel(source) {
  const rest = String(source).replace(/^howlett_?/, "").replace(/_/g, " ").trim();
  return rest ? `Howlett Farms — ${rest.replace(/\b\w/g, (c) => c.toUpperCase())}` : "Howlett Farms";
}
export function sourceLabel(source) {
  const s = String(source || "");
  if (/^howlett/.test(s)) return howlettLabel(s);
  return KNOWN_SOURCE_LABEL[s] || s;
}

// Which sources are a DEFENSIBLE cash reference for this crop.
//   soybeans — Howlett ONLY (Kevin, 2026-08-17: "Howlett Farms is currently
//     the only soybean basis/cash source available in Silas that should be
//     used for soybean recommendations"). Star of the West's generic scraper
//     CAN return a commodity='soy' row (its commodityFromBid() matches the ZS
//     root), but it has never been verified as a real, sellable local bid, so
//     it is deliberately withheld here rather than silently adopted.
//   corn — Howlett + WNY Energy (Medina). Both are boards this agency's own
//     growers actually truck to.
//   wheat — Howlett + Star of the West + ADM Buffalo (all three are
//     milling-wheat boards already wired for wheat specifically).
//   Ohio's Deerfield boards (source 'deerfield_*') are excluded for every
//     crop — not local, same exclusion api/_grain-brief.js already applies.
const ALLOWED_SOURCE = {
  soybeans: (src) => /^howlett/.test(src),
  corn: (src) => /^howlett/.test(src) || src === "wny_energy",
  wheat: (src) => /^howlett/.test(src) || src === "star_of_west" || src === "adm_buffalo",
};

// Round DOWN to the nearest practical lot (default 1,000 bu) — "Silas never
// recommends committing more grain than the deterministic risk calculation
// intended" (Kevin, 2026-08-17). 0 stays 0 (a real "nothing to sell" answer,
// not a missing one); anything below one lot also floors to 0 — the caller
// keeps the pre-rounded number separately so that distinction is visible.
export function roundDownToLot(bu, lot = 1000) {
  if (bu == null || isNaN(bu)) return bu;
  if (!(bu > 0)) return bu === 0 ? 0 : bu;
  return Math.floor(bu / lot) * lot;
}

// ── Basis history context (Kevin, 2026-08-17 reconciliation §B): "expose
// current basis percentile/rank over a STATED lookback and sample size. No
// black-box score, no arbitrary weights." Computed off whatever basisRows the
// caller already fetched for THIS source — no new fetch, no new table. The
// lookback/sample size reported are the REAL span and count found in the
// rows it was given, never assumed: a caller wiring only a 10-day basis
// window (today's App.jsx callers) gets an honestly-labeled ~10-day context,
// not a fabricated annual percentile. available:false + a stated reason when
// the sample is too thin to say anything, per spec §B's own escape hatch
// ("if the available history cannot support this cleanly, leave basis-
// context as unknown and do not block the vertical slice").
//
// Methodology (stated, not hidden): percentile = the share of this source's
// own historical basis readings that are AT OR BELOW today's basis. A HIGHER
// basis is stronger for a seller (cash = futures + basis), so a high
// percentile means today's basis is relatively strong for this source/crop;
// a low one means it is relatively weak. `context` is a plain three-way
// label off that percentile — not a weighted score.
// crop/bidStructure/deliveryContract (all optional, all additive to the
// 2026-08-17 signature): the SELECTED candidate's own crop/structure/
// delivery reference, threaded through filterComparableBasisHistory so a
// delivered row and a pickup row can never share one sample (docs/grain-
// gauge-build.md). bidStructure defaults to this source's own documented
// fact (SOURCE_BID_STRUCTURE) when the caller doesn't supply one, so an
// existing caller that only passes `source` keeps its prior behavior for a
// source with a known static structure (e.g. wny_energy) and degrades to
// UNKNOWN — i.e. comparable:0 — for a source with none, rather than silently
// re-widening the sample to "same source, any structure."
// structureBySource (optional): buildBidStructureIndex(boardRows) output —
// canonicalSourceKey -> delivered/pickup, read off grain_basis_board.term.
// A history row carrying its own explicit `term` (the transcribed seed rows
// do) still wins; the index only answers for rows that have none, which is
// every basis_calculated_daily row. Omitting it reproduces the old behavior
// exactly, so no existing caller changes meaning.
export function basisHistoryContext({ source, rows = [], currentBasis, minSamples = 5, crop = null, bidStructure = null, deliveryContract = null, structureBySource = null }) {
  if (currentBasis == null || !source) return { available: false, reason: "no current basis to compare" };
  const fromIndex = (s) => (structureBySource ? structureBySource.get(canonicalSourceKey(s)) || null : null);
  const structure = bidStructure || fromIndex(source) || resolveBidStructure(source, null);
  const candidateRows = (rows || [])
    .filter((r) => r && r.delivery_basis != null)
    .map((r) => ({ ...r, bidStructure: resolveBidStructure(r.source, r.term) || BID_STRUCTURE.UNKNOWN }))
    .map((r) => (r.bidStructure === BID_STRUCTURE.UNKNOWN
      ? { ...r, bidStructure: fromIndex(r.source) || BID_STRUCTURE.UNKNOWN }
      : r));
  const { comparable, excluded } = filterComparableBasisHistory({ crop, source, bidStructure: structure, deliveryContract, rows: candidateRows });
  const hist = comparable
    .map((r) => ({ v: Number(r.delivery_basis), d: r.bid_date || null }))
    .filter((r) => !isNaN(r.v));
  if (hist.length < minSamples) {
    return {
      available: false,
      reason: excluded.length && !hist.length
        ? `${excluded.length} historical row(s) on file for ${source}, but none share a confirmed comparable bid structure (${structure})`
        : `only ${hist.length} historical basis sample(s) on file for ${source} with a confirmed comparable bid structure (${structure}) (need ${minSamples})`,
      sampleSize: hist.length,
      bidStructure: structure,
    };
  }
  const dates = hist.map((r) => r.d).filter(Boolean).sort();
  const lookbackDays = dates.length >= 2
    ? Math.max(1, Math.round((new Date(dates[dates.length - 1]) - new Date(dates[0])) / 86400000))
    : null;
  const atOrBelow = hist.filter((r) => r.v <= currentBasis).length;
  const percentile = Math.round((atOrBelow / hist.length) * 100);
  const context = percentile >= 67 ? "strong" : percentile <= 33 ? "weak" : "normal";
  return { available: true, percentile, sampleSize: hist.length, lookbackDays, context, bidStructure: structure };
}

// crop: 'Corn' | 'Soybeans' | 'Wheat' (or lowercase / 'soy').
// contractDate: 'YYYY-MM-01', the same board-anchor date buildGrainAction
//   already resolves per crop year (computeGrainActionResult in App.jsx).
// basisRows: basis_calculated_daily rows — {source, delivery_contract,
//   delivery_basis, delivery_futures_price, cash_price, bid_date}.
// boardRows: grain_basis_board rows for this commodity — {location,
//   cme_contract, ship_month, basis, updated_at}.
// farmGeo: optional {lat, lon} — the farm's own coordinates (farmers.lat/lon,
//   the SAME columns PortalHomeView's cash-bid radius already reads). Kevin,
//   2026-08-17 reconciliation §A: "strongest basis alone must never make a
//   source eligible." When known, every candidate gets a real distanceMi and
//   an in-radius/out-of-radius flag using OPERATIONAL_RADIUS_MI (App.jsx's
//   own existing 30 mi default, not an invented cutoff), and an in-radius
//   candidate is preferred over a farther one regardless of price. When
//   farmGeo is absent, geography is reported `unknown` rather than assumed —
//   selection still runs (ALLOWED_SOURCE is already a geography-constrained
//   allowlist), but the result says plainly that no distance check ran.
// farmerProfile: optional { allowedSources: string[] } — an EXPLICIT,
// agent-confirmed list of source slugs this specific farm actually sells
// to. No table/UI backs this yet anywhere in the app (a real future seam,
// not built this session) — every live call today omits it, so
// logisticsConfidence below is "unknown" for every farm in production.
// Kevin, 2026-08-19 (mobile review): "Action still says HOWLETT FARMS —
// GENEVA for a farm that will never deliver to Geneva... Remove
// destination-specific execution language from the recommendation unless
// that destination is explicitly verified for that farm." The resolver
// still has to pick ONE candidate to price the economics off of (that part
// is unchanged — a real, dated, sourced number, never fabricated) but it
// must now say PLAINLY whether that candidate is a confirmed destination
// or just the best-ranked OBSERVED EXAMPLE, so a caller can render the two
// differently instead of stating an unconfirmed elevator as an instruction.
function resolveLogistics(source, farmerProfile) {
  const confirmed = !!(farmerProfile && Array.isArray(farmerProfile.allowedSources) && farmerProfile.allowedSources.includes(source));
  return { logisticsConfidence: confirmed ? "confirmed" : "unknown", exampleOnly: !confirmed };
}

export function resolveGrainCash({ crop, contractDate, basisRows = [], boardRows = [], farmGeo = null, farmerProfile = null }) {
  const cropKey0 = String(crop || "").trim().toLowerCase();
  const cropKey = cropKey0 === "soy" ? "soybeans" : cropKey0;
  const allow = ALLOWED_SOURCE[cropKey];
  if (!allow) {
    return { available: false, reason: `no defensible cash source is wired for "${crop}"`, candidates: [] };
  }

  const ladderCommodity = LADDER_COMMODITY[cropKey];
  let targetSuffix = null;
  if (contractDate) {
    const m = String(contractDate).match(/^(\d{4})-(\d{2})-\d{2}/);
    if (m && ladderCommodity) {
      const sym = grainBoardSymForMonth(ladderCommodity, Number(m[1]), Number(m[2]));
      targetSuffix = contractSuffix(sym);
    }
  }
  if (!targetSuffix) {
    return { available: false, reason: "no delivery contract could be resolved for this crop year", candidates: [] };
  }

  // ── delivery-PERIOD disambiguation ──────────────────────────────────────
  // src/App.jsx's own documented trap ("SEVERAL DELIVERY PERIODS PRICE OFF
  // ONE CONTRACT, at DIFFERENT basis" — Howlett posts Oct-Nov Z26 at one
  // basis AND 26-Dec Z26 at another, same bid date; Batavia posts Jan/Feb/
  // Mar '27 all off H27). Keying on the contract alone silently picks
  // whichever row happens to come first, which is exactly the bug this
  // reconciliation exists to close. Score each period label against the
  // TARGET contract's own calendar month + year — the identical weighting
  // App.jsx's pickBasis() score() uses when no explicit ship slot is chosen
  // — and keep only the best-scoring period per source; every other period
  // for that source is retained as periodAlternatives, never silently
  // dropped (2026-08-17 "NEXT WORK" reconciliation).
  const CME_LETTER_MONTH = { F: "JAN", G: "FEB", H: "MAR", J: "APR", K: "MAY", M: "JUN", N: "JUL", Q: "AUG", U: "SEP", V: "OCT", X: "NOV", Z: "DEC" };
  const targetMonthName = CME_LETTER_MONTH[targetSuffix[0]] || null;
  const targetYY = targetSuffix.slice(1);
  function periodScore(period) {
    const p = String(period || "").toUpperCase();
    return (targetMonthName && p.includes(targetMonthName) ? 2 : 0) + (p.includes(targetYY) ? 1 : 0);
  }
  // One row per SOURCE survives into `candidates` below; every other period
  // posted for that same source is folded into its periodAlternatives so the
  // choice stays auditable (spec §3/§7: "expose alternatives/provenance").
  // Grouped by source + bid structure (not source alone): the SAME Howlett
  // town can post both a DELIVERED bid and a FARM PICKUP bid for the same
  // crop/period (docs/grain-data-notes.md), and the live board reader below
  // derives `source` from location only. Grouping on structure too keeps
  // those two rows as two separate, auditable candidates instead of one
  // silently winning the "best period" contest against the other and hiding
  // which structure the survivor actually was.
  function pickBestPeriod(rows) {
    const bySource = new Map();
    for (const row of rows) {
      const groupKey = `${row.source}|${row.bidStructure || BID_STRUCTURE.UNKNOWN}`;
      const existing = bySource.get(groupKey);
      if (!existing) { bySource.set(groupKey, { best: row, others: [] }); continue; }
      const bs = periodScore(existing.best.deliveryPeriod), rs = periodScore(row.deliveryPeriod);
      if (rs > bs || (rs === bs && (row.asOf || "") > (existing.best.asOf || ""))) {
        existing.others.push(existing.best);
        existing.best = row;
      } else {
        existing.others.push(row);
      }
    }
    return [...bySource.values()].map(({ best, others }) => (
      others.length
        ? { ...best, periodAlternatives: others.map((o) => ({ deliveryPeriod: o.deliveryPeriod, basis: o.basis, price: o.price, asOf: o.asOf })) }
        : best
    ));
  }

  // basisRows already carries a computed cash_price for EVERY source it snapshots,
  // including Howlett's own daily snapshot (api/_howlett-basis.js — best-effort
  // cash from the posted basis + a USDA settle). Index it by exact source slug so
  // the live board below can reuse a REAL, already-computed futures reference
  // rather than inventing one — futures don't vary by elevator for the same
  // underlying CBOT contract, so borrowing the snapshot's futures leg to price
  // TODAY's live basis is a refresh, not a fabrication.
  const snapshotBySource = new Map();
  for (const r of basisRows || []) {
    if (!r || !r.source) continue;
    if (contractSuffix(r.delivery_contract) !== targetSuffix) continue;
    snapshotBySource.set(r.source, r);
  }

  // Bid structure straight off the board's own `term`, keyed canonically —
  // the ONE index every branch below and basisHistoryContext share, so a
  // candidate and its own history can never disagree about whether the
  // column is delivered or pickup (which is exactly what kept every Howlett
  // basis sample at zero until 2026-08-19).
  const structureIndex = buildBidStructureIndex(boardRows);

  // Howlett's LIVE sheet first — freshest basis, and the standing default (see
  // header). rank 0 always beats every scraped board.
  const boardCandidatesRaw = [];
  for (const r of boardRows || []) {
    if (r == null || r.basis == null || !r.cme_contract) continue;
    if (contractSuffix(r.cme_contract) !== targetSuffix) continue;
    const source = "howlett_" + String(r.location || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    if (!allow(source)) continue;
    const basisVal = Number(r.basis);
    const snap = snapshotBySource.get(source);
    const futures = snap && snap.delivery_futures_price != null ? Number(snap.delivery_futures_price) : null;
    // Prefer today's live basis + the snapshot's real futures leg; fall back to
    // the snapshot's own already-computed price only if no futures leg carried
    // through (never a fabricated number either way).
    const price = futures != null ? Math.round((futures + basisVal) * 10000) / 10000
      : (snap && snap.cash_price != null ? Number(snap.cash_price) : null);
    const townKey = howlettTown(r.location);
    boardCandidatesRaw.push({
      source, sourceLabel: sourceLabel(source),
      basis: basisVal, futures, price,
      deliveryPeriod: r.ship_month || null, deliveryContract: r.cme_contract,
      asOf: r.updated_at ? String(r.updated_at).slice(0, 10) : null,
      rank: 0, live: true,
      geo: townKey ? HOWLETT_TOWN[townKey] : null,
      // Read straight off grain_basis_board.term — never inferred (see the
      // BID_STRUCTURE header comment above).
      bidStructure: resolveBidStructure(source, r.term),
    });
  }
  const candidates = pickBestPeriod(boardCandidatesRaw);
  // Only a Howlett SOURCE (town) that resolved a real PRICE off the live
  // sheet is "already covered" — kept keyed on source alone, deliberately
  // NOT split by structure: the daily snapshot (api/_howlett-basis.js) never
  // records which term its basis reflects, so a snapshot row for a town the
  // live sheet has already priced is ambiguous either way and should stay
  // suppressed rather than compete against — or worse, silently outprice —
  // the live board's own confirmed-structure candidate.
  const howlettLivePriced = new Set(candidates.filter((c) => c.price != null).map((c) => c.source));
  const basisCandidatesRaw = [];
  for (const r of basisRows || []) {
    if (!r || !r.source) continue;
    // A transcribed archive row is HISTORY, not an offer standing today.
    // It carries no cash price and its date can be years old, so if a
    // source has no live candidate one of these would surface as that
    // farm's "observed example cash bid" at a basis from another crop
    // year. It still reaches basisHistoryContext below — that is its job.
    if (r.historyOnly) continue;
    // basis_calculated_daily carries no term field (verified against every
    // scraper that writes it), so its structure comes from the board's own
    // stated term for that location (structureIndex) and falls back to the
    // per-source fact table. Before 2026-08-19 this consulted only the fact
    // table, which knows one source — so every Howlett history row resolved
    // UNKNOWN while its live candidate resolved "delivered", and the two
    // could never be compared.
    // A row that STATES its own structure is believed FIRST — every archive
    // observation carries bid_structure. The board index is next (the live
    // sheet's own term), the one-source fact table last.
    const stated = normalizeBidStructure(r.term);
    const bidStructure = stated !== BID_STRUCTURE.UNKNOWN
      ? stated
      : (structureIndex.get(canonicalSourceKey(r.source)) || resolveBidStructure(r.source, null));
    if (/^howlett/.test(r.source) && howlettLivePriced.has(r.source)) continue;
    if (!allow(r.source)) continue;
    if (contractSuffix(r.delivery_contract) !== targetSuffix) continue;
    basisCandidatesRaw.push({
      source: r.source, sourceLabel: sourceLabel(r.source),
      basis: r.delivery_basis != null ? Number(r.delivery_basis) : null,
      futures: r.delivery_futures_price != null ? Number(r.delivery_futures_price) : null,
      price: r.cash_price != null ? Number(r.cash_price) : null,
      deliveryPeriod: r.delivery_period || null, deliveryContract: r.delivery_contract,
      asOf: r.bid_date || null,
      rank: /^howlett/.test(r.source) ? 0 : 1, live: false,
      geo: ELEVATOR_GEO[r.source] || null,
      bidStructure,
    });
  }
  candidates.push(...pickBestPeriod(basisCandidatesRaw));

  // De-dupe by source + bid structure — a Howlett column can appear once from
  // the live sheet and once from its own daily snapshot; keep whichever copy
  // actually resolved a price, and the live one otherwise (freshest basis for
  // audit). Keyed on structure too, for the same reason pickBestPeriod is.
  const bySource = new Map();
  for (const c of candidates) {
    const key = `${c.source}|${c.bidStructure || BID_STRUCTURE.UNKNOWN}`;
    const existing = bySource.get(key);
    if (!existing || (existing.price == null && c.price != null) || (existing.price == null && c.live)) {
      bySource.set(key, c);
    }
  }
  const dedupedCandidates = [...bySource.values()];

  // ── geography (Kevin, 2026-08-17 reconciliation §A) ─────────────────────
  // "known" only when the caller actually supplied farm coordinates — never
  // inferred, never defaulted to "assume local". Every candidate that has a
  // resolvable elevator/town geo gets a real haversine distanceMi; a source
  // this file has no coordinates for (a howlett location outside the four
  // known towns, or a source missing from ELEVATOR_GEO) gets distanceMi:null
  // and geographyEligible:false when geography IS known — an unlocatable
  // source is not treated as "close enough" by default.
  const geographyConfidence = (farmGeo && farmGeo.lat != null && farmGeo.lon != null) ? "known" : "unknown";
  for (const c of dedupedCandidates) {
    const dist = geographyConfidence === "known" && c.geo ? milesBetween(farmGeo, c.geo) : null;
    c.distanceMi = dist != null ? Math.round(dist * 10) / 10 : null;
    c.geographyEligible = geographyConfidence === "known" ? (c.distanceMi != null && c.distanceMi <= OPERATIONAL_RADIUS_MI) : null;
  }

  // A candidate needs a resolved CASH price to be selectable — a basis with no
  // matching futures settle yet stays in `candidates` for audit, never gets
  // treated as an executable price (never invent cash from basis alone).
  const priced = dedupedCandidates.filter((c) => c.price != null);
  // Geography-eligible candidates sort BEFORE ineligible ones whenever
  // geography is known — this is the "strongest basis alone must never make
  // a source eligible" rule as a sort key, not a filter, so an out-of-radius
  // source is still visible/selectable (never blanks the recommendation) but
  // never wins purely on price. rank/price stays the tiebreaker exactly as
  // before when geography is unknown or ties.
  const byPref = (a, b) => {
    if (geographyConfidence === "known") {
      const ae = a.geographyEligible ? 0 : 1, be = b.geographyEligible ? 0 : 1;
      if (ae !== be) return ae - be;
    }
    return (a.rank - b.rank) || (b.price - a.price);
  };
  priced.sort(byPref);
  dedupedCandidates.sort((a, b) => {
    if (geographyConfidence === "known") {
      const ae = a.geographyEligible ? 0 : 1, be = b.geographyEligible ? 0 : 1;
      if (ae !== be) return ae - be;
    }
    return (a.rank - b.rank) || ((b.price ?? -Infinity) - (a.price ?? -Infinity));
  });

  if (!priced.length) {
    const cropLabel = cropKey === "soybeans" ? "Howlett soybean" : `${cropKey} local`;
    return {
      available: false,
      reason: dedupedCandidates.length
        ? `${dedupedCandidates.length} basis source(s) posted for this delivery period, but none carry a resolved cash price yet`
        : `no ${cropLabel} basis source has posted this delivery period`,
      candidates: dedupedCandidates,
      geographyConfidence,
    };
  }
  const sel = priced[0];

  let geographyNote = null;
  if (geographyConfidence === "known" && sel.geographyEligible === false) {
    geographyNote = sel.distanceMi != null
      ? `nearest priced source (${sel.sourceLabel}) is ${sel.distanceMi} mi from the farm — outside the ${OPERATIONAL_RADIUS_MI} mi operational radius; no closer priced source was posted for this delivery period`
      : `${sel.sourceLabel} has no geocoded delivery point on file — distance to the farm is unknown, not assumed local`;
  } else if (geographyConfidence === "unknown") {
    geographyNote = "farm location not on file — selection used the verified source allowlist only; no distance check ran";
  }

  // Basis history context (§B), now comparability-gated (docs/grain-gauge-
  // build.md): computed off whatever basisRows the caller already passed in,
  // PLUS the static Kevin-transcribed HISTORICAL_BASIS_ROWS seed (2026-08-19
  // "basis runtime wiring" ask) — deepening the comparable sample beyond the
  // live ~10-day fetch window without ever letting a historical row compete
  // as a current cash candidate (candidates are built above from basisRows/
  // boardRows alone; this array is appended ONLY here). Restricted to rows
  // sharing this candidate's crop + source + bid structure + delivery
  // reference — never a delivered/pickup blend, live or historical alike.
  // available:false with a stated sample size (and the structure it needed)
  // when there isn't enough COMPARABLE history to say anything.
  const basisContext = sel.basis != null
    ? basisHistoryContext({ source: sel.source, rows: [...(basisRows || []), ...HISTORICAL_BASIS_ROWS], currentBasis: sel.basis, crop: cropKey, bidStructure: sel.bidStructure, deliveryContract: sel.deliveryContract, structureBySource: structureIndex })
    : { available: false, reason: "no basis on the selected candidate" };

  // logisticsConfidence/exampleOnly (2026-08-19): geography ("known farm
  // coordinates put this candidate in-radius") is NOT the same claim as
  // logistics ("this farm actually hauls/delivers here") — see
  // buildGrainConfidence's own header for why those stay separate chips.
  // Only an explicit farmerProfile match earns "confirmed"; everything else
  // — including every live call today, since no profile seam is wired up
  // yet — is an OBSERVED EXAMPLE bid: real, dated, sourced, but not this
  // farm's confirmed execution venue. otherPricedCandidates lets a caller
  // show alternatives instead of presenting one winner as "the" answer.
  const { logisticsConfidence, exampleOnly } = resolveLogistics(sel.source, farmerProfile);
  const priceLabel = exampleOnly
    ? `an observed example cash bid — ${sel.sourceLabel} is not a confirmed delivery destination for this farm`
    : `today's confirmed cash bid at ${sel.sourceLabel}`;

  return {
    available: true,
    price: sel.price, futures: sel.futures, basis: sel.basis,
    source: sel.source, sourceLabel: sel.sourceLabel,
    // Bid structure (docs/grain-gauge-build.md) — DELIVERED/PICKUP/DIRECT_SHIP/
    // UNKNOWN, read from the explicit field this candidate carried. Surfaced
    // so a caller can show it and so the basis-opportunity gauge can gate its
    // own comparability claim on it.
    bidStructure: sel.bidStructure || BID_STRUCTURE.UNKNOWN,
    deliveryPeriod: sel.deliveryPeriod, deliveryContract: sel.deliveryContract, asOf: sel.asOf,
    periodAlternatives: sel.periodAlternatives || [],
    candidates: dedupedCandidates,
    // otherPricedCandidates: every OTHER real, priced source besides the
    // winner — for a genuine side-by-side (docs/grain-gauge-build.md: "no
    // winner unless verified farm logistics/net freight are known"), not a
    // single silent pick.
    otherPricedCandidates: priced.slice(1).map((c) => ({ source: c.source, sourceLabel: c.sourceLabel, price: c.price, basis: c.basis, bidStructure: c.bidStructure, deliveryPeriod: c.deliveryPeriod, distanceMi: c.distanceMi })),
    geographyConfidence, distanceMi: sel.distanceMi, geographyEligible: sel.geographyEligible, geographyNote,
    logisticsConfidence, exampleOnly, priceLabel,
    basisContext,
  };
}
